import React from 'react';
import { TriangleIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
import { CRESTMONT, CrestmontHeader, CrestmontFooter } from '../components/crestmont/CrestmontChrome';
import { CrestmontLeadForm } from '../components/crestmont/CrestmontLeadForm';
import { BenefitsSection, CtaBand, FaqSection } from '../components/crestmont/CrestmontDetails';
import { crestmontPrograms } from '../data/crestmontPrograms';
import { getUniversityBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';

// Institution-level Crestmont University page, built to the same brand guide
// as the degree pages. Marketing copy comes from the existing Crestmont
// advertiser record so the two stay in sync; the degree cards deep-link into
// the BBA and AA-BA pages.
export function CrestmontUniversityPage() {
  const university = getUniversityBySlug('crestmont-university');
  const content = university?.landingContent;
  useMetaTags({
    title: 'Crestmont University — Online Degrees Built Around Your Life',
    description: content?.subheadline || 'Accredited online degree programs from Crestmont University.',
    robots: 'noindex, follow'
  });
  const scrollToForm = () => {
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const degrees = Object.values(crestmontPrograms);
  const programOptions = degrees.map(entry => ({
    label: entry.name,
    value: entry.mProgramValue
  }));
  return <div className="min-h-screen bg-white w-full" data-id="element-1506" data-mp="6edbpl43veg0">
      <CrestmontHeader onApply={scrollToForm} data-id="element-1507" data-mp="3or1osdb5gg8p" />

      <main data-id="element-1508" data-mp="3oscs6c0onlza">
        {/* Hero */}
        <section className="py-14 md:py-20" style={{
        backgroundColor: CRESTMONT.navy
      }} data-id="element-1509" data-mp="3ssda46mzqfb3">
          <div className="max-w-6xl mx-auto px-4 grid gap-10 lg:grid-cols-5 lg:items-start" data-id="element-1510" data-mp="3es2i309y2uoc">
            <div className="lg:col-span-3" data-id="element-1511" data-mp="3jrfby4z8h20f">
              <TriangleIcon className="w-5 h-5 mb-5" style={{
              color: CRESTMONT.gold
            }} fill={CRESTMONT.gold} aria-hidden="true" data-id="element-1512" data-mp="3jr5oao1nv6xw" />
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-[1.05] tracking-tight max-w-lg" data-id="element-1513" data-mp="1kjgvrnclazgt">
                Education tailored to your life.
              </h1>
              <p className="mt-5 text-sm md:text-base text-white/75 max-w-lg leading-relaxed" data-id="element-1514" data-mp="1blmzudiqm7u6">
                {content?.subheadline}
              </p>
              <ul className="mt-8 space-y-2.5 max-w-lg" data-id="element-1515" data-mp="2490ozpg71z3q">
                {(content?.heroHighlights || []).map(highlight => <li key={highlight} className="flex items-start gap-2.5" data-id="element-1516" data-mp="1z8ler6r7l7pz">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.gold
                }} aria-hidden="true" data-id="element-1517" data-mp="1ub30r1yc42b4" />
                    <span className="text-sm leading-relaxed text-white/80" data-id="element-1518" data-mp="iv4wrci0k6gr">
                      {highlight}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-2" data-id="element-1519" data-mp="drc9h98eve9a">
              <CrestmontLeadForm programValue={degrees[0].mProgramValue} programName="program you choose" programOptions={programOptions} heading="Request Program Information" subheading="Tell us where to reach you and a Crestmont advisor will follow up with program details, transfer credit, and next steps." data-id="element-1520" data-mp="do7v5j18f24w" />
            </div>
          </div>
        </section>

        {/* Stats strip */}
        {content?.stats && content.stats.length > 0 && <section className="border-b border-gray-200" style={{
        backgroundColor: CRESTMONT.mist
      }} aria-label="Crestmont at a glance" data-id="element-1521" data-mp="16e6rlzhra1jl">
            <dl className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1522" data-mp="38632r800h9du">
              {content.stats.map(stat => <div key={stat.label} className="text-center" data-id="element-1523" data-mp="12grce0l3hqku">
                  <dt className="sr-only" data-id="element-1524" data-mp="xk0t74dzuev7">{stat.label}</dt>
                  <dd data-id="element-1525" data-mp="xlka8x7h4ty1">
                    <span className="block text-2xl font-extrabold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1526" data-mp="1biqvcyfo74qf">
                      {stat.value}
                    </span>
                    <span className="mt-1 block text-[11px] text-gray-500" data-id="element-1527" data-mp="iv4pfjiyzo0e">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </section>}

        {/* Degree programs — the primary decision on this page */}
        <section className="py-16 md:py-20 bg-white" aria-labelledby="degrees-heading" data-id="element-1528" data-mp="nrv4q4myafkr">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1529" data-mp="1r13x9riftr">
            <h2 id="degrees-heading" className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1530" data-mp="3rawsu2odpxmy">
              Choose your business degree.
            </h2>
            <p className="mt-3 text-sm text-gray-600 max-w-xl" data-id="element-1531" data-mp="2bkxb5hd9w1fx">
              Both degrees run one course at a time in five-week terms. Every
              associate credit transfers into the bachelor’s, so starting
              smaller costs you nothing.
            </p>

            <ul className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-1532" data-mp="2qhegnrys6luk">
              {degrees.map(degree => <li key={degree.slug} className="flex" data-id="element-1533" data-mp="2uh571ukeazyk">
                  <a href={`/universities/crestmont-university/${degree.slug}`} className="group flex flex-col w-full border border-gray-200 rounded-lg p-6 transition-colors hover:border-[#00426b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00426b]" data-id="element-1534" data-mp="2gif8eahxgs83">
                    <span className="inline-flex self-start px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wide" style={{
                  backgroundColor: CRESTMONT.mist,
                  color: CRESTMONT.navy
                }} data-id="element-1535" data-mp="1a2mqzmt0rsoy">
                      {degree.degreeLevel}
                    </span>
                    <h3 className="mt-4 text-lg font-bold leading-snug" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1536" data-mp="c7j3885mjlyl">
                      {degree.name}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1537" data-mp="2c8w560dcody">
                      {degree.heroSubhead}
                    </p>
                    <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2" data-id="element-1538" data-mp="3pfm5dqu1fbc4">
                      <div data-id="element-1539" data-mp="1smaj5lll30r0">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1540" data-mp="1sliyv6xfspg1">
                          Credits
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1541" data-mp="1nlmv98snj010">
                          {degree.curriculum.totalCredits}
                        </dd>
                      </div>
                      <div data-id="element-1542" data-mp="24z5xeku0hz1b">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1543" data-mp="29weeei3fbx0z">
                          Standard plan
                        </dt>
                        <dd className="text-sm font-bold" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1544" data-mp="1ca8rziawfwpz">
                          {degree.stats[0].value}
                        </dd>
                      </div>
                      <div data-id="element-1545" data-mp="1q9gt4gqmhewt">
                        <dt className="text-[10px] uppercase tracking-wide text-gray-400" data-id="element-1546" data-mp="1q5kkdj1l1b3q">
                          Total tuition
                        </dt>
                        <dd className="text-sm font-bold tabular-nums" style={{
                      color: CRESTMONT.navy
                    }} data-id="element-1547" data-mp="31lrbrubnjunj">
                          {degree.requirements.tuition.total}
                        </dd>
                      </div>
                    </dl>
                    <span className="mt-auto pt-5 border-t border-gray-100 inline-flex items-center gap-1.5 text-sm font-bold" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1548" data-mp="1acnjw8dcnk1">
                      Explore the {degree.shortName}
                      <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1549" data-mp="5a3imc4xhpud" />
                    </span>
                  </a>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Why Crestmont */}
        {content?.features && content.features.length > 0 && <section className="py-16 md:py-20" style={{
        backgroundColor: CRESTMONT.mist
      }} aria-labelledby="why-heading" data-id="element-1550" data-mp="wi55m63u711p">
            <div className="max-w-5xl mx-auto px-4" data-id="element-1551" data-mp="11gf1kixdy5od">
              <h2 id="why-heading" className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{
            color: CRESTMONT.navy
          }} data-id="element-1552" data-mp="mt3m45y321iz">
                Why students choose Crestmont
              </h2>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-1553" data-mp="du0311yx1m4h">
                {content.features.map(feature => <li key={feature.title} className="bg-white rounded-lg p-6 border-l-2" style={{
              borderLeftColor: CRESTMONT.gold
            }} data-id="element-1554" data-mp="3tpq7igsmgpu">
                    <h3 className="text-sm font-bold" style={{
                color: CRESTMONT.navy
              }} data-id="element-1555" data-mp="1y77g3c4o7po1">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1556" data-mp="2fkhsm5avxm7m">
                      {feature.description}
                    </p>
                  </li>)}
              </ul>
            </div>
          </section>}

        {/* Long-form institutional content */}
        {content?.infoSections && content.infoSections.length > 0 && <section className="py-16 md:py-20 bg-white" data-id="element-1557" data-mp="2z90eoe0lgji3">
            <div className="max-w-3xl mx-auto px-4 space-y-12" data-id="element-1558" data-mp="2zabkejdlo1bu">
              {content.infoSections.map(section => <article key={section.heading} data-id="element-1559" data-mp="2pbcjjmz2t9ze">
                  {section.eyebrow && <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-1560" data-mp="2pdqabjmq4015">
                      {section.eyebrow}
                    </p>}
                  <h2 className="mt-2 text-xl md:text-2xl font-extrabold tracking-tight" style={{
              color: CRESTMONT.navy
            }} data-id="element-1561" data-mp="1dya1enhbwcd4">
                    {section.heading}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600" data-id="element-1562" data-mp="16d7kzduarrfj">
                    {section.body}
                  </p>
                  {section.bullets && <ul className="mt-4 space-y-2" data-id="element-1563" data-mp="1fdcqfifxkfr2">
                      {section.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1564" data-mp="rnied33fqv7v">
                          <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1565" data-mp="rlfja4nfebwa" />
                          <span className="text-sm leading-relaxed text-gray-700" data-id="element-1566" data-mp="wltpdh5gvbro">
                            {bullet}
                          </span>
                        </li>)}
                    </ul>}
                </article>)}
            </div>
          </section>}

        <CtaBand heading={content?.finalCta?.headline || 'Interested in learning more about our degrees?'} body={content?.finalCta?.body || ''} onApply={scrollToForm} data-id="element-1567" data-mp="2406xllnz6w6z" />
        <BenefitsSection benefits={degrees[0].benefits} data-id="element-1568" data-mp="2ipuxcigidmsl" />
        <FaqSection faq={degrees[0].faq} data-id="element-1569" data-mp="2irfr45v011pe" />
      </main>

      <CrestmontFooter data-id="element-1570" data-mp="1l2e04yw4fegz" />
    </div>;
}